/**
 * 사내결재 문서함 — [회의록] 탭 (v2.118.0 · 2026-09-03 · 사용자 요청)
 *                 + 참석자 동의 서명 (v2.229.0 · 2026-10-06 · 사용자 요청 「결재를 없애고 동의 서명으로」)
 *
 *   상단: [+ 회의록 작성]
 *   ① 진행 중인 내 회의록 — 작성중·서명 중 (내가 쓴 것 + 내가 참석자인 것)
 *      서명 n/m · 반영 안 된 변경 요청 · 「내 서명 필요」를 한 줄에 보여 준다
 *   ② 확정 보관 목록 — 서명 완료 (+ 결재로 끝난 옛 회의록)
 *
 * 가시성은 서버가 행 단위로 거른다:
 *   작성자 본인 + 부서장(자기 부서)·경영지원·대표이사 + admin
 *   + ⭐**참석자로 이름이 오른 사내 직원**
 *
 * ⭐주소 `?doc=번호` 로 들어오면 그 회의록을 바로 연다 (v2.229.0) — 웍스 서명 요청·변경 요청 알림의 링크.
 *   ⚠열고 나서 `doc` 를 주소에서 지운다(replace). 안 지우면 창을 닫아도 새로고침마다 다시 열린다.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import * as api from '../../lib/expense-api';
import { meetingStatusLabel } from '../../lib/meeting-api';
import { splitOpenMeetings } from '../../lib/meeting-list';
import { EXPENSE_STATUS_BADGE } from '../../components/ExpenseDocModal';
import { MeetingMinutesModal } from '../../components/MeetingMinutesModal';
import { openServerPdfPreview } from '../../lib/pdf-preview';
import { useAuth } from '../../lib/auth-context';
import { LoadingLines } from '../../components/ui/Skeleton';

const YEARS = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i);

/** 서명 n/m · 변경 요청 · 내 서명 필요 — 진행 중 목록의 한 칸 */
function SignProgress({ r }: { r: api.ExpenseDocListRow }) {
  const ms = r.meetingSign;
  if (!ms || ms.legacy || r.status === '작성중' || r.status === '반려') return null;
  return (
    <span className="inline-flex items-center gap-1 shrink-0">
      {ms.required > 0 && (
        <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 tabular-nums whitespace-nowrap">
          서명 {ms.signed}/{ms.required}
        </span>
      )}
      {ms.iMustSign && (
        <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold whitespace-nowrap">✍️ 내 서명 필요</span>
      )}
      {ms.openRequests > 0 && (
        <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 whitespace-nowrap">💬 변경 요청 {ms.openRequests}</span>
      )}
    </span>
  );
}

export function MeetingMinutesTab() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<{ id: number | null } | null>(null);

  // 웍스 알림 링크(?doc=번호)로 들어오면 그 회의록을 연다 — 열고 나서 주소에서 지운다
  const docParam = searchParams.get('doc');
  useEffect(() => {
    const id = Number(docParam);
    if (!docParam || !Number.isInteger(id) || id <= 0) return;
    setOpen({ id });
    const sp = new URLSearchParams(searchParams);
    sp.delete('doc');
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docParam]);

  // 확정(=보관) 목록 — 서명 완료 + 결재로 끝난 옛 회의록
  const finalQuery = useQuery({
    queryKey: ['expense', 'docs', 'meeting', 'final', year, q],
    queryFn: () => api.listExpenseDocs({ group: 'meeting', final: true, year, q: q.trim() || undefined }),
    staleTime: 15_000,
  });

  // 진행 중 — 서버는 부서장·대표이사 등에게 남의 진행 건도 준다 → 아래에서 「내 것」과 「대표이사 열람」으로 나눈다
  const openQuery = useQuery({
    queryKey: ['expense', 'docs', 'meeting', 'open'],
    queryFn: () => api.listExpenseDocs({ group: 'meeting', notFinal: true }),
    staleTime: 15_000,
  });
  // ⭐「내 것」 = 내가 쓴 것 + **내가 참석자로 올라 있는 것**.
  //   작성자만 보면 함께 회의한 사람에게는 목록이 비어 보인다 — 서버는 주고 있는데
  //   화면이 도로 걸러내는 실수(휴일근무일지 v2.102.1)를 되풀이하지 않는다.
  /**
   * 대표이사는 참석하지 않은 회의록도 서명 중일 때부터 본다(othersForCeo · v2.229.2 · 사용자 10-06
   * 「회의록 작성된 내용은 참가자가 아니더라도 대표이사(이승환)은 볼 수 있도록」).
   * 서버는 원래 대표이사에게 전부 주고 있었다 — 화면이 「내 것」만 남겨 서명 완료 전까지 안 보였다.
   * 열어도 보기만 한다(서명·변경 요청은 참석자 몫 — 서버가 막는다). 나누는 규칙은 lib/meeting-list.ts 한 곳.
   */
  const { mine, othersForCeo } = splitOpenMeetings(
    openQuery.data?.rows ?? [], user?.userId, openQuery.data?.viewerIsCeo,
  );
  const mustSign = mine.filter((r) => r.meetingSign?.iMustSign).length;

  const rows = finalQuery.data?.rows ?? [];

  return (
    <div className="space-y-5">
      <div className="bg-blue-50 border border-blue-200 rounded p-4 text-sm text-blue-900">
        <div className="font-semibold mb-1">회의록 — 참석자 동의 서명</div>
        <ul className="list-disc pl-5 space-y-0.5 text-[13px] text-blue-800">
          <li>회의 내용을 적고 <b>[✍️ 서명 요청]</b>을 누르면 사내 참석자에게 웍스 알림이 갑니다. <b>결재선은 없습니다.</b></li>
          <li>참석자는 내용을 읽고 동의하면 <b>[✍️ 동의 서명]</b>, 고칠 곳이 있으면 <b>[변경 요청]</b>을 댓글처럼 남깁니다.</li>
          <li>사내 참석자가 <b>모두 서명하면 「서명 완료」</b> — 아래 목록에 보관되고 📄 확정본의 참석자 표에 서명이 찍힙니다.</li>
          <li>서명을 받은 뒤 작성자가 내용(회의 자료 포함)을 고치면 <b>받은 서명은 지워지고 다시 서명</b>을 받습니다.</li>
          <li>외부 참석자는 계정이 없어 서명 대상이 아닙니다. 고치기·지우기는 작성한 사람만 합니다.</li>
        </ul>
      </div>

      <div>
        <button
          type="button"
          onClick={() => setOpen({ id: null })}
          className="btn btn-primary"
        >
          + 회의록 작성
        </button>
      </div>

      {/* ① 진행 중인 내 회의록 */}
      {mine.length > 0 && (
        <section>
          <h3 className="ui-subsection-title mb-2">
            진행 중인 내 회의록 <span className="text-slate-400 font-normal">{mine.length}건</span>
            {mustSign > 0 && (
              <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold">
                내 서명 필요 {mustSign}건
              </span>
            )}
          </h3>
          <div className="border border-slate-200 rounded overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 font-medium w-[120px]">문서번호</th>
                  <th className="text-left px-3 py-2 font-medium w-[96px]">상태</th>
                  <th className="text-left px-3 py-2 font-medium w-[110px]">작성일</th>
                  <th className="text-left px-3 py-2 font-medium">회의명</th>
                </tr>
              </thead>
              <tbody>
                {mine.map((r) => (
                  <tr key={r.id} onClick={() => setOpen({ id: r.id })}
                      className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                    <td className="px-3 py-2 font-mono text-xs">{r.docNo}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-1.5 py-0.5 rounded whitespace-nowrap ${EXPENSE_STATUS_BADGE[r.status] ?? ''}`}>
                        {meetingStatusLabel(r.status, r.meetingSign?.legacy ?? false)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-600">{r.docDate}</td>
                    <td className="px-3 py-2">
                      {/* 남이 쓴 회의록(내가 참석자로 들어간 것)은 누가 썼는지 밝힌다 */}
                      <div className="flex items-center gap-1.5 min-w-0">
                        {r.authorId !== user?.userId && (
                          <span className="shrink-0 text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                            {r.authorName} 작성
                          </span>
                        )}
                        <span className="truncate min-w-0">{r.purpose ?? ''}</span>
                        <SignProgress r={r} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ①-2 진행 중인 다른 회의록 — 대표이사만 (참석하지 않은 회의도 서명 중일 때부터 본다 · v2.229.2) */}
      {othersForCeo.length > 0 && (
        <section>
          <h3 className="ui-subsection-title mb-2">
            진행 중인 다른 회의록 <span className="text-slate-400 font-normal">{othersForCeo.length}건 · 대표이사 열람</span>
          </h3>
          <div className="border border-slate-200 rounded overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 font-medium w-[120px]">문서번호</th>
                  <th className="text-left px-3 py-2 font-medium w-[96px]">상태</th>
                  <th className="text-left px-3 py-2 font-medium w-[110px]">작성일</th>
                  <th className="text-left px-3 py-2 font-medium w-[92px]">작성자</th>
                  <th className="text-left px-3 py-2 font-medium min-w-[160px]">회의명</th>
                </tr>
              </thead>
              <tbody>
                {othersForCeo.map((r) => (
                  <tr key={r.id} onClick={() => setOpen({ id: r.id })}
                      className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                    <td className="px-3 py-2 font-mono text-xs">{r.docNo}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-1.5 py-0.5 rounded whitespace-nowrap ${EXPENSE_STATUS_BADGE[r.status] ?? ''}`}>
                        {meetingStatusLabel(r.status, r.meetingSign?.legacy ?? false)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-600">{r.docDate}</td>
                    <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{r.authorName}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="truncate min-w-0">{r.purpose ?? ''}</span>
                        <SignProgress r={r} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ② 확정 (보관) */}
      <section>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <h3 className="ui-subsection-title">
            서명 완료 <span className="text-slate-400 font-normal">{rows.length}건</span>
          </h3>
          <div className="flex items-center gap-2">
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}
              className="ui-input">
              {YEARS.map((y) => <option key={y} value={y}>{y}년</option>)}
            </select>
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="문서번호·작성자·회의명 검색…"
              className="ui-input w-60 max-w-full" />
          </div>
        </div>

        <div className="border border-slate-200 rounded overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 font-medium w-[120px]">문서번호</th>
                <th className="text-left px-3 py-2 font-medium w-[110px]">작성일</th>
                <th className="text-left px-3 py-2 font-medium w-[92px]">작성자</th>
                <th className="text-left px-3 py-2 font-medium w-[110px]">부서</th>
                <th className="text-left px-3 py-2 font-medium">회의명</th>
                <th className="px-3 py-2 font-medium w-[52px]"></th>
              </tr>
            </thead>
            <tbody>
              {finalQuery.isLoading ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400"><LoadingLines /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">
                  서명이 끝난 회의록이 없습니다.
                </td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} onClick={() => setOpen({ id: r.id })}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                  <td className="px-3 py-2 font-mono text-xs">{r.docNo}</td>
                  <td className="px-3 py-2 text-slate-600">{r.docDate}</td>
                  <td className="px-3 py-2">{r.authorName}</td>
                  <td className="px-3 py-2 text-slate-600">{r.authorDept ?? ''}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="truncate min-w-0">{r.purpose ?? ''}</span>
                      {/* 결재로 끝난 옛 회의록 — 서명이 아니라 결재로 확정됐다는 표시 */}
                      {r.meetingSign?.legacy && (
                        <span className="shrink-0 text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">결재 완료(옛 방식)</span>
                      )}
                      {!r.meetingSign?.legacy && (r.meetingSign?.openRequests ?? 0) > 0 && (
                        <span className="shrink-0 text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                          💬 변경 요청 {r.meetingSign!.openRequests}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {r.attachmentId && (
                      <button type="button" title="문서 보기"
                        onClick={(e) => {
                          e.stopPropagation();
                          openServerPdfPreview({
                            pdfUrl: api.attachmentDownloadUrl(r.attachmentId!),
                            filename: `${r.docNo}_회의록.pdf`,
                          });
                        }}
                        className="text-slate-400 hover:text-blue-600">📄</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {open && <MeetingMinutesModal docId={open.id} onClose={() => setOpen(null)} />}
    </div>
  );
}
